import { useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, LogOut } from "lucide-react";

import { ADMINISTRATION_NAV_GROUPS, type AdministrationNavItem } from "@/features/administration/constants";
import { useFeatureOn } from "@/features/feature-switches/featureSwitchSession";
import { useGovernancePendingExceptionCount } from "@/features/administration/hooks/useGovernancePendingExceptionCount";
import { useAdminWorkspacesModalStore } from "@/stores/adminWorkspacesModalStore";
import { Header } from "@/components/shell/Header";
import { useAuth } from "@/state/AuthContext";
import { isPlatformAdmin } from "@/utils/access";
import { AdminVersionFooter } from "@/features/administration/layout/AdminVersionFooter";

export default function AdministrationLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const workspacesModalOpen = useAdminWorkspacesModalStore((s) => s.isOpen);
  const openWorkspacesModal = useAdminWorkspacesModalStore((s) => s.open);
  const { user, logout } = useAuth();
  const isAdmin = isPlatformAdmin(user);
  const teamsOn = useFeatureOn("teams");
  const ruleEngineOn = useFeatureOn("governance-rule-engine");
  const { count: pendingExceptionsCount } = useGovernancePendingExceptionCount(isAdmin);
  const switchOn: Record<string, boolean> = {
    teams: teamsOn,
    "governance-rule-engine": ruleEngineOn,
  };

  const navWidth = useMemo(() => (collapsed ? "w-16" : "w-64"), [collapsed]);
  const location = useLocation();

  const isNavItemActive = (item: AdministrationNavItem): boolean => {
    if (item.search) {
      return location.pathname === item.path && location.search === item.search;
    }
    const pathMatch =
      location.pathname === item.path ||
      (item.alsoMatchPaths?.includes(location.pathname) ?? false);
    if (!pathMatch) return false;
    // People & Teams stays active for any tab query on /administration/people.
    if (item.path === "/administration/people") return true;
    return (
      !location.search.includes("tab=exceptions") &&
      !location.search.includes("tab=approvals")
    );
  };

  const visibleNavGroups = useMemo(
    () =>
      ADMINISTRATION_NAV_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) => !item.featureKey || switchOn[item.featureKey] === true),
      }))
        .filter((group) => group.items.length > 0)
        .filter((g) => !g.adminOnly || isAdmin),
    [isAdmin, teamsOn, ruleEngineOn],
  );

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 dark:bg-slate-950">
      {/*
       * Administration routes sit outside DashboardLayout (admin sidebar replaces the main shell),
       * but the app header with profile, notifications, and command palette should stay reachable.
       */}
      <Header />

      {/*
       * Viewport width drives Tailwind breakpoints. Docked DevTools shrinks the *page*
       * width (often to ~500–900px), which is still “desktop” — `lg`/`md` falsely flipped
       * many users to the mobile hint. Keep the hint for narrow *phones* only (≲ typical
       * portrait width), not for “narrow because DevTools”.
       */}
      <div className="flex flex-1 p-6 min-[480px]:hidden">
        <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700">
          {isAdmin
            ? "Administration tools are best used on desktop."
            : "Settings are best used on desktop."}
        </div>
      </div>

      <div className="hidden min-h-0 flex-1 min-[480px]:flex">
        <aside className={`${navWidth} shrink-0 border-r border-gray-200 bg-white transition-all`}>
          {/*
           * Admin Console MVP-1 — "Back to Zephix" exit affordance.
           * Per Admin Console Architecture Spec v1 §4.1: every Admin Console
           * page must offer a clear way back to the operational app. /inbox
           * is the universal post-login landing per the same spec, so it's
           * the safest target. When the sidebar is collapsed, only the
           * ArrowLeft icon shows; when expanded, the icon + label show.
           *
           * Use a full document navigation (native <a>) so leaving Administration
           * remounts DashboardLayout and shell state; React Router <Link> alone
           * can leave stale layout when switching sibling route trees.
           */}
          <a
            href="/inbox"
            data-testid="admin-back-to-zephix"
            title={collapsed ? "Back to Zephix" : undefined}
            className={`flex items-center gap-2 border-b border-gray-200 px-3 py-2 text-sm text-gray-500 transition-colors hover:text-gray-900 ${
              collapsed ? "justify-center" : ""
            }`}
          >
            <ArrowLeft className="h-4 w-4 shrink-0" />
            {!collapsed ? <span>Back to Zephix</span> : null}
          </a>
          <div className="flex h-14 items-center justify-between border-b border-gray-200 px-3">
            {!collapsed ? (
              <div>
                <p className="text-sm font-semibold text-gray-900">Settings & Administration</p>
                <p className="text-xs text-gray-500">
                  {isAdmin ? "Account and organization" : "Your account"}
                </p>
              </div>
            ) : (
              <p className="text-xs font-semibold text-gray-700">S&A</p>
            )}
            <button
              type="button"
              className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              aria-label={
                collapsed
                  ? "Expand Settings & Administration navigation"
                  : "Collapse Settings & Administration navigation"
              }
              onClick={() => setCollapsed((prev) => !prev)}
            >
              {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </button>
          </div>

          <nav className="p-2 space-y-3 overflow-y-auto" style={{ maxHeight: "calc(100vh - 120px)" }}>
            {visibleNavGroups.map((group, groupIdx) => (
              <div key={group.label}>
                {groupIdx > 0 && <hr className="mb-2 border-gray-200" />}
                {!collapsed && (
                  <span className="mb-1 block px-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                    {group.label}
                  </span>
                )}
                {group.items.map((item) => {
                  return item.opensWorkspacesModal ? (
                    <button
                      key={`${item.path}-modal`}
                      type="button"
                      title={collapsed ? item.label : undefined}
                      aria-current={workspacesModalOpen ? "page" : undefined}
                      onClick={openWorkspacesModal}
                      className={`mb-0.5 flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors ${
                        workspacesModalOpen
                          ? "bg-gray-100 font-medium text-gray-900"
                          : "text-gray-700 hover:bg-gray-50"
                      } ${collapsed ? "justify-center" : ""}`}
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      {!collapsed ? <span>{item.label}</span> : null}
                    </button>
                  ) : item.search ? (
                    <Link
                      key={`${item.path}${item.search}`}
                      to={{ pathname: item.path, search: item.search }}
                      title={collapsed ? item.label : undefined}
                      className={`mb-0.5 flex items-center gap-2 rounded px-2 py-1.5 text-sm transition-colors ${
                        isNavItemActive(item)
                          ? "bg-gray-100 font-medium text-gray-900"
                          : "text-gray-700 hover:bg-gray-50"
                      } ${collapsed ? "justify-center" : ""}`}
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      {!collapsed ? <span className="flex-1">{item.label}</span> : null}
                      {item.pendingExceptionsBadge && pendingExceptionsCount > 0 ? (
                        <span
                          className="ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold text-white"
                          data-testid="admin-nav-exceptions-badge"
                        >
                          {pendingExceptionsCount}
                        </span>
                      ) : null}
                    </Link>
                  ) : item.path === "/administration/governance" ? (
                    <Link
                      key={item.path}
                      to="/administration/governance"
                      title={collapsed ? item.label : undefined}
                      className={`mb-0.5 flex items-center gap-2 rounded px-2 py-1.5 text-sm transition-colors ${
                        isNavItemActive(item)
                          ? "bg-gray-100 font-medium text-gray-900"
                          : "text-gray-700 hover:bg-gray-50"
                      } ${collapsed ? "justify-center" : ""}`}
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      {!collapsed ? <span>{item.label}</span> : null}
                    </Link>
                  ) : (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end
                      title={collapsed ? item.label : undefined}
                      className={({ isActive }) =>
                        `mb-0.5 flex items-center gap-2 rounded px-2 py-1.5 text-sm transition-colors ${
                          isActive
                            ? "bg-gray-100 font-medium text-gray-900"
                            : "text-gray-700 hover:bg-gray-50"
                        } ${collapsed ? "justify-center" : ""}`
                      }
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      {!collapsed ? <span>{item.label}</span> : null}
                    </NavLink>
                  );
                })}
              </div>
            ))}
          </nav>
          <div className="border-t border-gray-200 p-2">
            <button
              type="button"
              data-testid="admin-sidebar-logout"
              title={collapsed ? "Log out" : undefined}
              onClick={() => void logout()}
              className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 ${
                collapsed ? "justify-center" : ""
              }`}
            >
              <LogOut className="h-4 w-4 shrink-0" />
              {!collapsed ? <span>Log out</span> : null}
            </button>
          </div>
        </aside>

        <section className="min-w-0 flex-1 select-text overflow-auto bg-gray-50 p-6">
          <Outlet />
        </section>
      </div>
      {isAdmin ? <AdminVersionFooter /> : null}
    </div>
  );
}
